import React, { useState, useEffect } from 'react'
import {
  Autocomplete,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Grid,
  Slider,
} from '@mui/material'
import { useRedirect, useNotify, useGetList } from 'react-admin'
import { getJson, postJson, getUrl } from '../../providers/data'
import { GemeenteResponse, GebiedsprognoseConfig } from '../../types/apiRequests'
import { GebiedsprognoseFormData, defaultFormData } from '../../types/gebiedsprognoseTypes'

const steps = [
  'Basis Instellingen',
  'Gebiedscodes',
  'Parameters',
  'Overzicht',
]

const GebiedsprognoseCreate: React.FC = () => {
  const redirect = useRedirect()
  const notify = useNotify()

  const [gemeenteSearch, setGemeenteSearch] = useState('')
  const { data: gemeenten = [], isLoading: gemeentenLoading } = useGetList<GemeenteResponse>('gemeenten', {
    sort: { field: 'naam', order: 'ASC' },
    pagination: { page: 1, perPage: 25 },
    filter: gemeenteSearch ? { q: gemeenteSearch } : {},
  })
  const [selectedGemeente, setSelectedGemeente] = useState<GemeenteResponse | null>(null)

  const [activeStep, setActiveStep] = useState(0)
  const [formData, setFormData] = useState<GebiedsprognoseFormData>(defaultFormData)
  const [config, setConfig] = useState<GebiedsprognoseConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load config when gemeente changes
  useEffect(() => {
    if (!formData.gemeente_code) return

    const fetchConfig = async () => {
      setLoading(true)
      try {
        const result = await getJson<GebiedsprognoseConfig>(
          `${getUrl()}/gemeenten/${formData.gemeente_code}/gebiedsprognose/config`
        )
        setConfig(result)
        if (result.max_bjaar) {
          setFormData((prev) => ({
            ...prev,
            bjaar: result.max_bjaar ?? prev.bjaar,
          }))
        }
      } catch (err) {
        console.error('Failed to load config:', err)
        setError(`Kan configuratie niet laden: ${String(err)}`)
      } finally {
        setLoading(false)
      }
    }
    void fetchConfig()
  }, [formData.gemeente_code])

  // When stadsprognose is selected, inherit bjaar/ejaar
  useEffect(() => {
    if (!formData.sp_id || !config?.stadsprognoses) return
    const sp = config.stadsprognoses.find((s: Record<string, unknown>) => s.id === formData.sp_id)
    if (sp) {
      setFormData((prev) => ({
        ...prev,
        bjaar: sp.bjaar as number,
        ejaar: sp.ejaar as number,
        naam: prev.naam || `Gebiedsprognose ${sp.naam as string}`,
      }))
    }
  }, [formData.sp_id, config])

  const handleChange = (updates: Partial<GebiedsprognoseFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }))
  }

  const handleNext = () => setActiveStep((prev) => prev + 1)
  const handleBack = () => setActiveStep((prev) => prev - 1)

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const result = await postJson<{ id: string; status: string }>(
        `${getUrl()}/gemeenten/${formData.gemeente_code}/gebiedsprognose`,
        {
          naam: formData.naam,
          omschrijving: formData.omschrijving,
          sp_id: formData.sp_id,
          bjaar: formData.bjaar,
          ejaar: formData.ejaar,
          gebiedsvoorkeur_code: formData.gebiedsvoorkeur_code,
          leeftijdsprofiel_code: formData.leeftijdsprofiel_code,
          gwb: formData.gwb,
          woningbouwplanning_code: formData.woningbouwplanning_code,
          minmax_code: formData.minmax_code,
          aanname_mfmp: formData.aanname_mfmp,
          aanname_fc: formData.aanname_fc,
          migratiecorrectie: formData.migratiecorrectie,
          grens_woningtoename: formData.grens_woningtoename,
          fc_max: formData.fc_max,
          fc_daling: formData.fc_daling,
          nieuwbouw_demping: formData.nieuwbouw_demping,
        }
      )
      notify('Gebiedsprognose berekening gestart', { type: 'info' })
      redirect('show', 'gebiedsprognoses', result.id)
    } catch (err) {
      setError(`Fout bij starten berekening: ${String(err)}`)
      notify('Fout bij starten berekening', { type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const isStepValid = (): boolean => {
    switch (activeStep) {
      case 0:
        return !!formData.naam && !!formData.gemeente_code && !!formData.sp_id && formData.ejaar > formData.bjaar
      case 1:
      case 2:
      case 3:
        return true
      default:
        return false
    }
  }

  const stadsprognoses = config?.stadsprognoses ?? []

  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Basis Instellingen</Typography>

      <TextField
        fullWidth
        label="Naam"
        value={formData.naam}
        onChange={(e) => handleChange({ naam: e.target.value })}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Omschrijving"
        value={formData.omschrijving}
        onChange={(e) => handleChange({ omschrijving: e.target.value })}
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      {stadsprognoses.length > 0 ? (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Stadsprognose</InputLabel>
          <Select
            value={formData.sp_id}
            label="Stadsprognose"
            onChange={(e) => handleChange({ sp_id: e.target.value })}
          >
            {stadsprognoses.map((sp: Record<string, unknown>) => (
              <MenuItem key={sp.id as string} value={sp.id as string}>
                {sp.naam as string} ({sp.bjaar as number} - {sp.ejaar as number})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : config ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Geen voltooide stadsprognoses gevonden. Maak eerst een stadsprognose aan.
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Beginjaar"
            value={formData.bjaar}
            onChange={(e) => handleChange({ bjaar: parseInt(e.target.value, 10) })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Eindjaar"
            value={formData.ejaar}
            onChange={(e) => handleChange({ ejaar: parseInt(e.target.value, 10) })}
          />
        </Grid>
      </Grid>
    </Box>
  )

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Gebiedscodes</Typography>

      {(config?.gebiedsvoorkeuren ?? []).length > 0 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Gebiedsvoorkeur</InputLabel>
          <Select
            value={formData.gebiedsvoorkeur_code}
            label="Gebiedsvoorkeur"
            onChange={(e) => handleChange({ gebiedsvoorkeur_code: e.target.value })}
          >
            <MenuItem value="">Geen</MenuItem>
            {config!.gebiedsvoorkeuren!.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.naam || c.code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(config?.leeftijdsprofielen ?? []).length > 0 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Leeftijdsprofiel</InputLabel>
          <Select
            value={formData.leeftijdsprofiel_code}
            label="Leeftijdsprofiel"
            onChange={(e) => handleChange({ leeftijdsprofiel_code: e.target.value })}
          >
            <MenuItem value="">Geen</MenuItem>
            {config!.leeftijdsprofielen!.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.naam || c.code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(config?.woningbouwplanningen ?? []).length > 0 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Woningbouwplanning</InputLabel>
          <Select
            value={formData.woningbouwplanning_code}
            label="Woningbouwplanning"
            onChange={(e) => handleChange({ woningbouwplanning_code: e.target.value })}
          >
            <MenuItem value="">Geen</MenuItem>
            {config!.woningbouwplanningen!.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.naam || c.code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(config?.minmax_codes ?? []).length > 0 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Min/Max code</InputLabel>
          <Select
            value={formData.minmax_code}
            label="Min/Max code"
            onChange={(e) => handleChange({ minmax_code: e.target.value })}
          >
            <MenuItem value="">Geen</MenuItem>
            {config!.minmax_codes!.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.naam || c.code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <TextField
        fullWidth
        type="number"
        label="GWB (Gewenste Woningbezetting)"
        value={formData.gwb}
        onChange={(e) => handleChange({ gwb: parseFloat(e.target.value) })}
        inputProps={{ step: 0.1, min: 1.0, max: 5.0 }}
        sx={{ mb: 2 }}
      />
    </Box>
  )

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Parameters</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Aanname MFMP</InputLabel>
            <Select
              value={formData.aanname_mfmp}
              label="Aanname MFMP"
              onChange={(e) => handleChange({ aanname_mfmp: e.target.value })}
            >
              <MenuItem value="3">3 jaar</MenuItem>
              <MenuItem value="5">5 jaar</MenuItem>
              <MenuItem value="10">10 jaar</MenuItem>
              <MenuItem value="all">Alle jaren</MenuItem>
              <MenuItem value="last">Laatste jaar</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Aanname FC</InputLabel>
            <Select
              value={formData.aanname_fc}
              label="Aanname FC"
              onChange={(e) => handleChange({ aanname_fc: e.target.value })}
            >
              <MenuItem value="3">3 jaar</MenuItem>
              <MenuItem value="5">5 jaar</MenuItem>
              <MenuItem value="10">10 jaar</MenuItem>
              <MenuItem value="all">Alle jaren</MenuItem>
              <MenuItem value="last">Laatste jaar</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="subtitle2" gutterBottom>MinMax Parameters</Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Migratiecorrectie: {formData.migratiecorrectie}
        </Typography>
        <Slider
          value={formData.migratiecorrectie}
          onChange={(_, v) => handleChange({ migratiecorrectie: v as number })}
          min={0} max={2} step={0.05} valueLabelDisplay="auto"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Grens woningtoename: {formData.grens_woningtoename}
        </Typography>
        <Slider
          value={formData.grens_woningtoename}
          onChange={(_, v) => handleChange({ grens_woningtoename: v as number })}
          min={0.5} max={2} step={0.05} valueLabelDisplay="auto"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          FC Max: {formData.fc_max}
        </Typography>
        <Slider
          value={formData.fc_max}
          onChange={(_, v) => handleChange({ fc_max: v as number })}
          min={0.5} max={2} step={0.01} valueLabelDisplay="auto"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          FC Daling: {formData.fc_daling}
        </Typography>
        <Slider
          value={formData.fc_daling}
          onChange={(_, v) => handleChange({ fc_daling: v as number })}
          min={0} max={0.1} step={0.005} valueLabelDisplay="auto"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Nieuwbouw demping: {formData.nieuwbouw_demping}
        </Typography>
        <Slider
          value={formData.nieuwbouw_demping}
          onChange={(_, v) => handleChange({ nieuwbouw_demping: v as number })}
          min={0} max={1} step={0.05} valueLabelDisplay="auto"
        />
      </Box>
    </Box>
  )

  const renderStep4 = () => {
    const selectedSp = stadsprognoses.find((s: Record<string, unknown>) => s.id === formData.sp_id)
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Overzicht</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Naam</Typography>
            <Typography>{formData.naam}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Periode</Typography>
            <Typography>{formData.bjaar} - {formData.ejaar}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Stadsprognose</Typography>
            <Typography>{(selectedSp?.naam as string) || formData.sp_id}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">GWB</Typography>
            <Typography>{formData.gwb}</Typography>
          </Grid>
          {formData.gebiedsvoorkeur_code && (
            <Grid item xs={6}>
              <Typography variant="subtitle2">Gebiedsvoorkeur</Typography>
              <Typography>{formData.gebiedsvoorkeur_code}</Typography>
            </Grid>
          )}
          {formData.leeftijdsprofiel_code && (
            <Grid item xs={6}>
              <Typography variant="subtitle2">Leeftijdsprofiel</Typography>
              <Typography>{formData.leeftijdsprofiel_code}</Typography>
            </Grid>
          )}
          {formData.woningbouwplanning_code && (
            <Grid item xs={6}>
              <Typography variant="subtitle2">Woningbouwplanning</Typography>
              <Typography>{formData.woningbouwplanning_code}</Typography>
            </Grid>
          )}
          {formData.minmax_code && (
            <Grid item xs={6}>
              <Typography variant="subtitle2">Min/Max code</Typography>
              <Typography>{formData.minmax_code}</Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            <Typography variant="subtitle2">Aanname MFMP</Typography>
            <Typography>{formData.aanname_mfmp} jaar</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Aanname FC</Typography>
            <Typography>{formData.aanname_fc} jaar</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">MinMax Parameters</Typography>
            <Typography variant="body2">
              Migratiecorrectie: {formData.migratiecorrectie} |
              Grens woningtoename: {formData.grens_woningtoename} |
              FC Max: {formData.fc_max} |
              FC Daling: {formData.fc_daling} |
              Nieuwbouw demping: {formData.nieuwbouw_demping}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    )
  }

  const renderStep = () => {
    switch (activeStep) {
      case 0: return renderStep1()
      case 1: return renderStep2()
      case 2: return renderStep3()
      case 3: return renderStep4()
      default: return null
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Nieuwe Gebiedsprognose</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Autocomplete
          options={gemeenten}
          getOptionLabel={(option) => `${option.naam} (${option.gm_code})`}
          loading={gemeentenLoading}
          value={selectedGemeente}
          onInputChange={(_e, value) => setGemeenteSearch(value)}
          onChange={(_e, value) => {
            setSelectedGemeente(value)
            handleChange({ gemeente_code: value?.gm_code || '' })
          }}
          isOptionEqualToValue={(option, value) => option.gm_code === value.gm_code}
          renderInput={(params) => (
            <TextField {...params} label="Gemeente" fullWidth />
          )}
        />
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2">Configuratie laden...</Typography>
          </Box>
        )}
      </Paper>

      {formData.gemeente_code && config && (
        <>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper sx={{ p: 3, mb: 2 }}>
            {renderStep()}
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Vorige
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => void handleSubmit()}
                  disabled={submitting || !isStepValid()}
                  startIcon={submitting ? <CircularProgress size={20} /> : undefined}
                >
                  Prognose berekenen
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isStepValid()}
                >
                  Volgende
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default GebiedsprognoseCreate
